/**
 * Utility function to construct the full image URL from the backend
 * Handles both full URLs and relative paths
 */
export const getImageUrl = (imagePath: string | null | undefined): string | undefined => {
  if (!imagePath) {
    return undefined;
  }

  // Inline images (base64 data URIs from the WYSIWYG editor) and blob/object
  // URLs are already self-contained. Never rewrite them — running them through
  // the relative-path logic below mangles the base64 payload, because it
  // contains "/" and "//" sequences that get treated as path segments.
  if (imagePath.startsWith('data:') || imagePath.startsWith('blob:')) {
    return imagePath;
  }

  // If the path is already resolved with the local /api/v2/ prefix, strip it
  // to get the clean relative path, preventing recursive double-prefixing.
  if (imagePath.startsWith('/api/v2/')) {
    imagePath = imagePath.substring(7);
  }

  // If it's already a full URL, handle it directly
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    // Collapse any doubled slashes in the path (e.g. backend bug returning
    // "/uploads/logos//uploads/logos/file.jpg" should become
    // "/uploads/logos/uploads/logos/file.jpg" — keeps the URL valid even if
    // the path is still wrong; the next/image runtime error goes away)
    let url = imagePath.replace(/(?<!:)\/\/+/g, '/');
    return url;
  }

  // Get the API base URL (fall back to current domain in browser if NEXT_PUBLIC_API_URL is missing)
  let API_BASE_URL = process.env.NEXT_PUBLIC_API_URL;
  if (!API_BASE_URL && typeof window !== 'undefined') {
    const isLocal = window.location.hostname === 'localhost' || 
                    window.location.hostname.endsWith('.localhost') || 
                    window.location.hostname === '127.0.0.1';
    if (!isLocal) {
      API_BASE_URL = `${window.location.origin}/api/v2`;
    }
  }
  if (!API_BASE_URL) {
    API_BASE_URL = "http://localhost:5100/api/v2";
  }
  const baseURL = API_BASE_URL;

  // Normalise backslashes to forward slashes (for Windows paths)
  const normalizedPath = imagePath.replace(/\\/g, '/');

  // Handle paths like "/uploads/..." or "uploads/..."
  let finalPath = normalizedPath.startsWith('/') ? normalizedPath : `/${normalizedPath}`;

  // Resolve dot-segments (..) to clean up paths like /uploads/questions/../../uploads/
  const segments = finalPath.split('/');
  const resolvedSegments: string[] = [];
  for (const segment of segments) {
    if (segment === '.' || segment === '') continue;
    if (segment === '..') {
      if (resolvedSegments.length > 0) resolvedSegments.pop();
    } else {
      resolvedSegments.push(segment);
    }
  }
  finalPath = '/' + resolvedSegments.join('/');

  // Clean up duplicate uploads segments (e.g. /uploads/trainers//uploads/trainers/ or /uploads/logos/uploads/logos/)
  finalPath = finalPath.replace(/\/uploads\/trainers\/+uploads\/trainers\//g, "/uploads/trainers/");
  finalPath = finalPath.replace(/\/uploads\/logos\/+uploads\/logos\//g, "/uploads/logos/");
  finalPath = finalPath.replace(/\/uploads\/uploads\//g, "/uploads/");
  finalPath = finalPath.replace(/(\/uploads\/[^\/]+)\/+\1\//g, "$1/");

  // Special case for question bank: if it doesn't start with /uploads/,
  // it might be a raw filename that needs /uploads/questions/ prefix
  if (!finalPath.startsWith('/uploads/')) {
    if (
      finalPath === '/instacks-logo.png' || 
      finalPath.startsWith('/images/') ||
      finalPath.startsWith('/templates/') ||
      finalPath.startsWith('/template-previews/') ||
      finalPath.startsWith('/assets/') ||
      finalPath.startsWith('/icons/')
    ) {
      return finalPath;
    }
    finalPath = `/uploads/questions${finalPath}`;
  }

  // If the path starts with /uploads/, return it as a relative path.
  // In production (non-localhost), route via Nginx proxy using the /api/v2 prefix.
  if (finalPath.startsWith('/uploads/')) {
    const isLocalHostUrl = baseURL.includes('localhost') || baseURL.includes('127.0.0.1');
    const isLocalhost = isLocalHostUrl || (
      typeof window !== 'undefined' && (
        window.location.hostname === 'localhost' || 
        window.location.hostname.endsWith('.localhost') || 
        window.location.hostname === '127.0.0.1'
      )
    );
    if (!isLocalhost) {
      return `/api/v2${finalPath}`.replace(/(?<!:)\/\/+/g, '/');
    }
    // On localhost, return the full URL so frontend (port 3000) can reach backend (port 5100)
    // Strip "/api/v2" so it matches the allowed next.config.js remote pattern `/uploads/**`
    const cleanBaseURL = baseURL.replace(/\/api\/v2\/?$/, '');
    return `${cleanBaseURL}${finalPath}`.replace(/(?<!:)\/\/+/g, '/');
  }

  // Collapse any doubled slashes in the path (see comment above)
  return `${baseURL}${finalPath}`.replace(/(?<!:)\/\/+/g, '/');
};

/**
 * Get image URL for paragraph questions
 */
export const getParaQuestionImageUrl = (imagePath: string | null | undefined): string | undefined => {
  if (!imagePath) {
    return undefined;
  }
  return getImageUrl(imagePath);
};

/**
 * Get image URL for text questions
 */
export const getTextQuestionImageUrl = (imagePath: string | null | undefined): string | undefined => {
  if (!imagePath) {
    return undefined;
  }
  return getImageUrl(imagePath);
};

/**
 * Processes HTML content and replaces relative image paths with absolute URLs
 */
export const processHtmlImages = (html: string | null | undefined): string => {
  if (!html) return '';

  // Find all src="..." or src='...' attributes in img tags
  return html.replace(/(<img[^>]+src=["'])([^"']+)((["'][^>]*>))/ig, (match, prefix, src, suffix) => {
    // If it's a data URI or already an absolute URL, leave it alone
    if (src.startsWith('data:') || src.startsWith('http://') || src.startsWith('https://')) {
      return match;
    }

    // Otherwise get the absolute URL using getImageUrl
    const absoluteUrl = getImageUrl(src);
    return `${prefix}${absoluteUrl || src}${suffix}`;
  });
};

/**
 * Render text safely as HTML for an MCQ option label.
 *
 * Most option text is plain (e.g. "Paris"). But MCQ options for programming
 * questions legitimately contain angle-bracketed strings like `<class 'int'>`,
 * `<class 'list'>`, etc. — those ARE the answer text, not markup. Rendering
 * them through `dangerouslySetInnerHTML` makes the browser eat the text as a
 * broken HTML tag.
 *
 * Strategy: if the text contains a recognised inline markup tag, pass it
 * through `processHtmlImages` as before. Otherwise escape it so angle brackets
 * display as text. ponytail: real fix is at render sites, not a sanitiser dep.
 */
const HTML_TAG_WHITELIST = /<\s*(div|math|mfrac|mrow|mi|mo|mn|msqrt|mstyle|msub|msubsup|msup|mtext|img|br|p|span|b|i|strong|em|u|code|pre|sub|sup|table|thead|tbody|tfoot|tr|td|th|ul|ol|li|h[1-6]|a|svg|hr|blockquote)\b/i;

export function safeOptionHtml(text: string | null | undefined): string {
  if (!text) return "";
  if (HTML_TAG_WHITELIST.test(text)) {
    return processHtmlImages(text);
  }
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * Utility function to resolve full URL for videos, PDFs, and document files
 */
export function resolveFileUrl(file_path: string | null | undefined): string {
  if (!file_path) return "";
  const trimmed_path = file_path.trim();
  if (
    trimmed_path.startsWith("http://") ||
    trimmed_path.startsWith("https://") ||
    trimmed_path.startsWith("//") ||
    trimmed_path.startsWith("data:") ||
    trimmed_path.startsWith("blob:")
  ) {
    return trimmed_path;
  }
  const api_url =
    process.env.NEXT_PUBLIC_API_URL || "http://localhost:5100/api/v2";
  const base_url = api_url.replace(/\/api\/v2\/?$/, "");
  return `${base_url}${trimmed_path.startsWith("/") ? "" : "/"}${trimmed_path}`;
}


